/**
 * Fixed pixel height for the BarChart so the widget never collapses to
 * zero in containers that don't impose their own height (CSS grid /
 * intrinsic-sized rows). Tuned to match the original heatmap silhouette.
 */
export const BAR_CHART_HEIGHT_PX = 160;

/**
 * Formats a 24h hour integer (0-23) as a left-padded two-digit string
 * (`"09"`, `"17"`, …) for axis labels.
 */
export const formatHourLabel = (hour: number): string => {
    return hour.toString().padStart(2, '0');
};

/**
 * Maps the API's raw percentage delta to the trend direction understood by
 * the trend Chip (and matches `getPercentageTrend` in revenueSummary's
 * meta — the rule is identical, duplicated here to keep widget files
 * self-contained).
 */
export type TrendDirection = 'down' | 'neutral' | 'up';

export const getPercentageTrend = (
    percentage: number | null,
): TrendDirection | null => {
    if (percentage === null) {
        return null;
    }
    if (percentage > 0) {
        return 'up';
    }
    if (percentage < 0) {
        return 'down';
    }
    return 'neutral';
};

/**
 * Chip colour per trend direction. Matches the `Chip` component's
 * `color` enum (success / danger / default).
 */
export const TREND_CHIP_COLOR: Record<
    TrendDirection,
    'default' | 'danger' | 'success'
> = {
    up: 'success',
    down: 'danger',
    neutral: 'default',
};

/**
 * Iconify identifier for the arrow shown inside the trend chip. Solar
 * pack matches the rest of the Sales widgets' icon vocabulary.
 */
export const TREND_CHIP_ICON: Record<TrendDirection, string> = {
    up: 'solar:arrow-up-bold',
    down: 'solar:arrow-down-bold',
    neutral: 'solar:arrow-right-bold',
};

/**
 * Raw API percent values arrive as percent points (e.g. `12.5` for
 * "+12.5%"). `Intl.NumberFormat({ style: "percent" })` expects a fraction
 * (e.g. `0.125`), so the View passes values through this helper before
 * handing them to `NumberValue`.
 */
export const toFractionalPercent = (percentage: number): number => {
    return percentage / 100;
};
